// src/pages/Answers/Answers.jsx
import { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Tooltip,
  Alert,
  styled,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  RadioGroup,
  FormControlLabel as MuiFormControlLabel,
  Radio,
  MenuItem,
  Select,
  FormHelperText
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Description as FormIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Preview as PreviewIcon,
  Search as SearchIcon,
  ClearAll as ClearAllIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  PictureAsPdf as PdfIcon,
  Assignment
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CalculationField from '../../components/form-builder/fields/CalculationField';

// Local imports
import { useAnswers, useFormEdit, useFormDelete, usePDFExport } from './hooks/useAnswers';
import DeleteFormDialog from './components/DeleteFormDialog';
import { FileDisplay, SignatureDisplay } from './components/FileComponents';
import FileEditField from './components/FileEditField';
import * as XLSX from "xlsx"
import axios from 'axios';
import { useSelector } from 'react-redux';
import { saveAs } from 'file-saver';
// Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: 'calc(100vh - 200px)',
  '& .MuiTableCell-root': {
    padding: theme.spacing(1.5, 2),
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: theme.palette.action.hover,
  }
}));

function renderCalculationField(field, index, editingResponse, setEditingResponse) {
  let calculationValue = field.value;
  if (typeof calculationValue === 'string') {
    try {
      calculationValue = JSON.parse(calculationValue);
    } catch {
      calculationValue = { columns: [], rows: [] };
    }
  }
  return (
    <CalculationField
      field={field}
      value={calculationValue}
      onChange={(val) => {
        const updatedFields = [...editingResponse.formFields];
        updatedFields[index].value = val;
        setEditingResponse({
          ...editingResponse,
          formFields: updatedFields
        });
      }}
      formData={editingResponse}
    />
  );
}

const Analytics = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Ana veriler ve filtreler
  const {
    loading,
    error,
    responses,
    folders,
    filteredResponses,
    startDate,
    endDate,
    searchTerm,
    setStartDate,
    setEndDate,
    setSearchTerm,
    loadResponses,
    clearFilters,
    setError
  } = useAnswers();

  // Form düzenleme
  const {
    editingResponse,
    editDialogOpen,
    setEditingResponse,
    setEditDialogOpen,
    handleEditResponse,
    handleSaveForm
  } = useFormEdit();

  // Form silme
  const {
    deleteDialogOpen,
    responseToDelete,
    deleteExplanation,
    deleteExplanationError,
    setDeleteExplanation,
    setDeleteExplanationError,
    handleDeleteResponse,
    openDeleteDialog,
    closeDeleteDialog
  } = useFormDelete();

  // PDF Export
  const { handleExportToPDF } = usePDFExport();

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);


  const createExcel = (ddata) => {
    const worksheet = XLSX.utils.json_to_sheet(ddata, {
      header: Object.keys(ddata[0]),
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "HHOMS Form Export");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, "data.xlsx");
  };

  const exportExcel = (data) => {
    if (!data || data.length === 0) return;

    const ddata = data.map((formData) => {
      const item = {};

      Object.keys(formData.formData.formData).forEach((key) => {
        const field = formData.formData.formData[key];
        if (field.type === "address" ||
          field.type === "gauge" ||
          field.type === "progress" ||
          field.type === "file" ||
          field.type === "image" ||
          field.type === "pdfDocument" ||
          field.type === "social-links" ||
          field.type === "signature" ||
          field.type === "drawing" ||
          field.type === "qr-code" ||
          field.type === "carbon-calculator" ||
          field.type === "eco-impact-display" ||
          field.type === "sustainability-score" ||
          field.type === "green-certificate" ||
          field.type === "table" ||
          field.type === "calculation" ||
          field.type === "staticText"
        ) {
          return;
        }
        if (field.type === "name") {
          item[field.label] = `${field.value.firstName} ${field.value.lastName}`;
        } else if (field.type === "toggle") {
          item[field.label] = field.value ? "Aktif" : "Pasif";
        } else if (field.type === "checkbox" || field.type === "range-slider") {
          item[field.label] = Array.isArray(field.value) ? field.value.join(", ") : field.value;
        } else {
          item[field.label] = field.value;
        }
      });

      return item;
    });

    createExcel(ddata);
  };


  // Preview işlemi için fonksiyonu güncelle
  const handlePreview = (response) => {
    navigate(`/form-response-preview/${response.id}`);
  };

  // Event handlers
  const onEditResponse = async (response) => {
    try {
      await handleEditResponse(response);
    } catch (error) {
      setError(error.message);
    }
  };

  const onSaveForm = async (updatedFields) => {
    await handleSaveForm(updatedFields, loadResponses);
  };

  const onDeleteResponse = async () => {
    await handleDeleteResponse(loadResponses);
  };

  // Yükleme durumu
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const getTableRenderData = (response) => {
    let formData = response.formData.formData;

    let ArrayifiedObjects = Object.keys(formData).map((key) => {
      return formData[key];
    });

    let hasNameField = ArrayifiedObjects.find((field) => field.type === "name");
    if (hasNameField) {
      return `${hasNameField.value.firstName || ''} ${hasNameField.value.lastName || ''}`;
    } else {
      let firstTextField = ArrayifiedObjects.find((field) => field.type === "text");
      console.log(firstTextField)
      if (firstTextField) {
        return firstTextField.value || '-';
      } else {
        return "-"
      }
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Başlık ve Kontroller */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        mt: 3,
        pt: 2,
        borderTop: '1px solid #eee',
        borderBottom: '1px solid #eee',
        paddingBottom: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ mr: 2 }}>
            {t('form_responses')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Assignment />}
              onClick={() => {
                console.log("Excel Export clicked");
                console.log(filteredResponses);

                const tempData = filteredResponses.map(response => {
                  return response;
                })
                exportExcel(tempData)
                // filteredResponses.forEach((response, idx) => {
                //   axios.get("https://app.hhoms.com/api/form/LibraryRecords/" + response.id, {
                //     headers: {
                //       "Content-Type": "application/json",
                //       "ApplicationToken": appToken ? appToken : "mete"
                //     }
                //   }).then((resp) => {
                //     let tmp = resp.data.formLibraryRecords[0];
                //     let ddata = JSON.parse(tmp.formContent);
                //     tempData.push(ddata);
                //     if (idx === filteredResponses.length - 1) {
                //       console.log(tempData)
                //       exportExcel(tempData);
                //     }
                //   })
                // })
              }}
            >
              Excel Export
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={loadResponses}
              size="medium"
            >
              {t('refresh')}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Filtreleme Alanları */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <Tooltip title={t('search_tooltip')} arrow placement="top">
              <TextField
                fullWidth
                label={t('search_form')}
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Form başlığı, kullanıcı, form ID veya klasör ismi ile arayın"
                InputProps={{
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                }}
              />
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Tooltip title={t('start_date_tooltip')} arrow placement="top">
              <TextField
                fullWidth
                label={t('start_date')}
                type="date"
                variant="outlined"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Tooltip title={t('end_date_tooltip')} arrow placement="top">
              <TextField
                fullWidth
                label={t('end_date')}
                type="date"
                variant="outlined"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={t('clear_filters')} arrow placement="top">
                <Button
                  maxWidth="100px"
                  variant="outlined"
                  color="primary"
                  onClick={clearFilters}
                  startIcon={<ClearAllIcon />}
                >
                  {t('clear')}
                </Button>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Hata Mesajı */}
      {error && (
        <Alert
          severity={error.type || 'error'}
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error.message}
        </Alert>
      )}

      {/* Yanıt Tablosu - responses yerine filteredResponses kullanın */}
      <Paper>
        <StyledTableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{t('form_title')}</TableCell>
                <TableCell>-</TableCell>
                <TableCell>Klasör</TableCell>
                <TableCell>{t('user')}</TableCell>
                <TableCell>{t('created_at')}</TableCell>
                <TableCell>{t('updated_at')}</TableCell>
                <TableCell>Form ID</TableCell>
                <TableCell align="right">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredResponses.map((response) => (
                <TableRow key={response.id} className={"custom-row"}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormIcon color="primary" />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {response.title || t('untitled_form')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getTableRenderData(response)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {response.parentId && response.parentId.trim() !== '' && response.parentId !== '0' ? (() => {
                      const parentIds = response.parentId.split(',').map(id => id.trim()).filter(id => id !== '' && id !== '0');
                      const foundFolders = parentIds
                        .map(id => folders[id])
                        .filter(folderName => folderName)
                        .filter((folderName, index, arr) => arr.indexOf(folderName) === index);

                      if (foundFolders.length > 0) {
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <FolderIcon color="action" fontSize="small" sx={{ fontSize: 16 }} />
                            {foundFolders.map((folderName, index) => (
                              <Chip
                                key={index}
                                label={folderName}
                                size="small"
                                color="default"
                                variant="outlined"
                                sx={{
                                  height: 22,
                                  fontSize: '0.75rem',
                                  '& .MuiChip-label': { px: 1 }
                                }}
                              />
                            ))}
                          </Box>
                        );
                      }
                      return '-';
                    })() : '-'}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      try {
                        if (response.user && response.user !== '') {
                          const userInfo = JSON.parse(response.user);
                          return userInfo.name || userInfo.email || 'Kullanıcı';
                        }
                        return '-';
                      } catch (error) {
                        return response.user || '-';
                      }
                    })()}
                  </TableCell>
                  <TableCell>
                    {new Date(response.createDate).toLocaleString('tr-TR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </TableCell>
                  <TableCell>
                    {response.editDate ? new Date(response.editDate).toLocaleString('tr-TR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : '-'}
                  </TableCell>
                  <TableCell>{response.formId}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Tooltip title={t('edit')}>
                        <IconButton
                          size="small"
                          onClick={() => onEditResponse(response)}
                          sx={{
                            bgcolor: 'secondary.main',
                            color: 'white',
                            '&:hover': {
                              bgcolor: 'secondary.dark',
                            }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('preview')}>
                        <IconButton
                          size="small"
                          onClick={() => handlePreview(response)}
                          sx={{
                            bgcolor: 'success.main',
                            color: 'white',
                            '&:hover': {
                              bgcolor: 'success.dark',
                            }
                          }}
                        >
                          <PreviewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="PDF Export">
                        <IconButton
                          size="small"
                          onClick={() => handleExportToPDF(response)}
                          sx={{
                            bgcolor: 'warning.main',
                            color: 'white',
                            '&:hover': {
                              bgcolor: 'warning.dark',
                            }
                          }}
                        >
                          <PdfIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('delete')}>
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(response)}
                          sx={{
                            bgcolor: 'error.main',
                            color: 'white',
                            '&:hover': {
                              bgcolor: 'error.dark',
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {filteredResponses.length === 0 && (
                <TableRow className={"custom-row"}>
                  <TableCell colSpan={7} align="center">
                    {loading ? t('loading') : t('no_results')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </Paper>

      {/* Yanıt Detay Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #eee'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormIcon color="primary" />
            {selectedResponse?.formTitle || 'Form Detayları'}
          </Box>
          <IconButton onClick={() => setDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {selectedResponse && (
            <Box sx={{ py: 2 }}>
              {/* Doküman Kontrol Bilgileri */}
              {selectedResponse.settings?.documentControlEnabled && (
                <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Doküman Kontrol Bilgileri
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {selectedResponse.settings.documentPrefix && selectedResponse.settings.documentCode && (
                      <Chip
                        label={`${selectedResponse.settings.documentPrefix}-${selectedResponse.settings.documentCode}`}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    )}
                    {selectedResponse.settings.department && (
                      <Chip
                        label={selectedResponse.settings.department}
                        color="secondary"
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Box>
                </Paper>
              )}

              {/* Form Başlık ve Açıklama */}
              <Typography variant="h5" gutterBottom>
                {selectedResponse.formTitle}
              </Typography>
              {selectedResponse.formDescription && (
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  {selectedResponse.formDescription}
                </Typography>
              )}

              {/* Form Alanları ve Yanıtlar */}
              {selectedResponse.formFields.map((field, index) => (
                <Paper
                  key={index}
                  sx={{
                    p: 2,
                    mb: 2,
                    border: '1px solid #e0e0e0'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      {field.label}
                    </Typography>
                    <Chip
                      label={field.type.toUpperCase()}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>

                  {/* Alan Değeri */}
                  <Box sx={{ mt: 2 }}>
                    {field.type === 'file' ? (
                      <FileEditField
                        field={field}
                        value={field.value}
                        onChange={val => {
                          const updatedFields = [...selectedResponse.formFields];
                          updatedFields[index].value = val;
                          setSelectedResponse({ ...selectedResponse, formFields: updatedFields });
                        }}
                      />
                    ) : field.type === 'signature' ? (
                      <Box sx={{
                        p: 2,
                        bgcolor: '#f5f5f5',
                        borderRadius: 1,
                        border: '1px solid #e0e0e0'
                      }}>
                        <Typography color="text.secondary" sx={{ mb: 1 }}>
                          İmza alanı
                        </Typography>
                        {field.value && (
                          <Box
                            component="img"
                            src={field.value}
                            alt="İmza"
                            sx={{
                              maxWidth: '100%',
                              height: 'auto',
                              border: '1px solid #ddd',
                              borderRadius: 4
                            }}
                          />
                        )}
                      </Box>
                    ) : field.type === 'calculation' ? (
                      renderCalculationField(field, index, editingResponse, setEditingResponse)
                    ) : field.type === 'name' ? (
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          fullWidth
                          label="Ad"
                          value={field.value && typeof field.value === 'object' ? field.value.firstName || '' : ''}
                          onChange={(e) => {
                            const updatedFields = [...editingResponse.formFields];
                            const currentValue = field.value && typeof field.value === 'object' ? field.value : { firstName: '', lastName: '' };
                            updatedFields[index].value = {
                              ...currentValue,
                              firstName: e.target.value
                            };
                            setEditingResponse({
                              ...editingResponse,
                              formFields: updatedFields
                            });
                          }}
                          variant="outlined"
                          size="small"
                        />
                        <TextField
                          fullWidth
                          label="Soyad"
                          value={field.value && typeof field.value === 'object' ? field.value.lastName || '' : ''}
                          onChange={(e) => {
                            const updatedFields = [...editingResponse.formFields];
                            const currentValue = field.value && typeof field.value === 'object' ? field.value : { firstName: '', lastName: '' };
                            updatedFields[index].value = {
                              ...currentValue,
                              lastName: e.target.value
                            };
                            setEditingResponse({
                              ...editingResponse,
                              formFields: updatedFields
                            });
                          }}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    ) : (
                      <Box sx={{
                        mt: 2,
                        p: 2,
                        bgcolor: '#f5f5f5',
                        borderRadius: 1,
                        border: '1px solid #e0e0e0'
                      }}>
                        {(() => {
                          switch (field.type) {
                            case 'checkbox':
                              return Array.isArray(field.value) ? (
                                <Box>
                                  {field.value.map((item, i) => (
                                    <Chip
                                      key={i}
                                      label={item}
                                      sx={{ m: 0.5 }}
                                    />
                                  ))}
                                </Box>
                              ) : <Typography>Seçim yapılmadı</Typography>;

                            case 'date':
                              return field.value ? (
                                <Typography>
                                  {new Date(field.value).toLocaleDateString('tr-TR')}
                                </Typography>
                              ) : <Typography>Tarih seçilmedi</Typography>;

                            case 'time':
                              return field.value ? (
                                <Typography>{field.value}</Typography>
                              ) : <Typography>Saat seçilmedi</Typography>;

                            case 'email':
                              return field.value ? (
                                <Typography component="a" href={`mailto:${field.value}`}>
                                  {field.value}
                                </Typography>
                              ) : <Typography>E-posta girilmedi</Typography>;

                            case 'phone':
                              return field.value ? (
                                <Typography>{field.value}</Typography>
                              ) : <Typography>Telefon girilmedi</Typography>;

                            case 'table':
                              return (
                                <Box sx={{
                                  mt: 2,
                                  p: 2,
                                  bgcolor: '#f5f5f5',
                                  borderRadius: 1,
                                  border: '1px solid #e0e0e0'
                                }}>
                                  <TableContainer>
                                    <Table size="small">
                                      <TableBody>
                                        {field.value?.rows?.map((row, rowIndex) => (
                                          <TableRow key={rowIndex}>
                                            {Object.values(row).map((cell, cellIndex) => (
                                              <TableCell key={cellIndex}>
                                                {cell}
                                              </TableCell>
                                            ))}
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </Box>
                              );

                            default:
                              return field.value ? (
                                <Typography>{typeof field.value === 'object' ? JSON.stringify(field.value) : String(field.value || '')}</Typography>
                              ) : <Typography>Yanıt verilmedi</Typography>;
                          }
                        })()}
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))}

              {/* İmza Alanı - Sadece form içinde imza alanı yoksa ve imza varsa göster */}
              {selectedResponse.signature && !selectedResponse.formFields.some(f => f.type === 'signature') && (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    İmza
                  </Typography>
                  <SignatureDisplay signature={selectedResponse.signature} />
                </Paper>
              )}

              {/* Dosyalar - Sadece form içinde dosya alanı yoksa ve dosyalar varsa göster */}
              {Object.keys(selectedResponse.files || {}).length > 0 && !selectedResponse.formFields.some(f => f.type === 'file') && (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Yüklenen Dosyalar
                  </Typography>
                  <List>
                    {Object.entries(selectedResponse.files).map(([key, file]) => (
                      <ListItem key={key}>
                        <ListItemIcon>
                          <AttachFileIcon />
                        </ListItemIcon>
                        <ListItemText primary={file.name} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #eee', p: 2 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            variant="outlined"
            startIcon={<CloseIcon />}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Düzenleme modalını ekle */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #eee'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon color="secondary" />
            {editingResponse?.formTitle || 'Form Düzenle'}
          </Box>
          <IconButton onClick={() => setEditDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {editingResponse && (
            <Box sx={{ py: 2 }}>
              {/* Doküman Kontrol Bilgileri */}
              {editingResponse.settings?.documentControlEnabled && (
                <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Doküman Kontrol Bilgileri
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Doküman Kodu"
                      value={editingResponse.settings.documentPrefix || ''}
                      onChange={(e) => {
                        setEditingResponse(prev => ({
                          ...prev,
                          settings: {
                            ...prev.settings,
                            documentPrefix: e.target.value
                          }
                        }));
                      }}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Doküman Numarası"
                      value={editingResponse.settings.documentCode || ''}
                      onChange={(e) => {
                        setEditingResponse(prev => ({
                          ...prev,
                          settings: {
                            ...prev.settings,
                            documentCode: e.target.value
                          }
                        }));
                      }}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Departman"
                      value={editingResponse.settings.department || ''}
                      onChange={(e) => {
                        setEditingResponse(prev => ({
                          ...prev,
                          settings: {
                            ...prev.settings,
                            department: e.target.value
                          }
                        }));
                      }}
                      size="small"
                      fullWidth
                    />
                  </Box>
                </Paper>
              )}

              <Typography variant="h5" gutterBottom>
                {editingResponse.formTitle}
              </Typography>
              {editingResponse.formDescription && (
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  {editingResponse.formDescription}
                </Typography>
              )}

              {editingResponse.formFields.map((field, index) => (
                <Paper
                  key={index}
                  sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      {field.label}
                    </Typography>
                    <Chip
                      label={field.type.toUpperCase()}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    {(field.type === 'file' || field.type === 'image') ? (
                      <FileEditField
                        field={field}
                        value={field.value}
                        onChange={val => {
                          const updatedFields = [...editingResponse.formFields];
                          updatedFields[index].value = val;
                          setEditingResponse({ ...editingResponse, formFields: updatedFields });
                        }}
                      />
                    ) : field.type === 'calculation' ? (
                      (() => {
                        let calculationValue = field.value;
                        if (typeof calculationValue === 'string') {
                          try {
                            calculationValue = JSON.parse(calculationValue);
                          } catch {
                            calculationValue = { columns: [], rows: [] };
                          }
                        }
                        return (
                          <CalculationField
                            field={field}
                            value={calculationValue}
                            onChange={(val) => {
                              const updatedFields = [...editingResponse.formFields];
                              updatedFields[index].value = val;
                              setEditingResponse({
                                ...editingResponse,
                                formFields: updatedFields
                              });
                            }}
                            formData={editingResponse}
                          />
                        );
                      })()
                    ) : (
                      (() => {
                        switch (field.type) {
                          case 'text':
                          case 'textarea':
                            return (
                              <TextField
                                fullWidth
                                multiline={field.type === 'textarea'}
                                rows={field.type === 'textarea' ? 4 : 1}
                                value={field.value || ''}
                                onChange={(e) => {
                                  const updatedFields = [...editingResponse.formFields];
                                  updatedFields[index].value = e.target.value;
                                  setEditingResponse({
                                    ...editingResponse,
                                    formFields: updatedFields
                                  });
                                }}
                                variant="outlined"
                                size="small"
                                placeholder={field.placeholder || ''}
                                helperText={field.helpText || ''}
                              />
                            );

                          case 'number':
                            return (
                              <TextField
                                fullWidth
                                type="number"
                                value={field.value || ''}
                                onChange={(e) => {
                                  const updatedFields = [...editingResponse.formFields];
                                  updatedFields[index].value = e.target.value;
                                  setEditingResponse({
                                    ...editingResponse,
                                    formFields: updatedFields
                                  });
                                }}
                                variant="outlined"
                                size="small"
                                placeholder={field.placeholder || ''}
                                helperText={field.helpText || ''}
                                inputProps={{
                                  min: field.min,
                                  max: field.max,
                                  step: field.step || 1
                                }}
                              />
                            );

                          case 'date':
                            return (
                              <TextField
                                fullWidth
                                type="date"
                                value={field.value || ''}
                                onChange={(e) => {
                                  const updatedFields = [...editingResponse.formFields];
                                  updatedFields[index].value = e.target.value;
                                  setEditingResponse({
                                    ...editingResponse,
                                    formFields: updatedFields
                                  });
                                }}
                                variant="outlined"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                  min: field.min,
                                  max: field.max
                                }}
                              />
                            );

                          case 'time':
                            return (
                              <TextField
                                fullWidth
                                type="time"
                                value={field.value || ''}
                                onChange={(e) => {
                                  const updatedFields = [...editingResponse.formFields];
                                  updatedFields[index].value = e.target.value;
                                  setEditingResponse({
                                    ...editingResponse,
                                    formFields: updatedFields
                                  });
                                }}
                                variant="outlined"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                              />
                            );

                          case 'email':
                            return (
                              <TextField
                                fullWidth
                                type="email"
                                value={field.value || ''}
                                onChange={(e) => {
                                  const updatedFields = [...editingResponse.formFields];
                                  updatedFields[index].value = e.target.value;
                                  setEditingResponse({
                                    ...editingResponse,
                                    formFields: updatedFields
                                  });
                                }}
                                variant="outlined"
                                size="small"
                                placeholder={field.placeholder || ''}
                                helperText={field.helpText || ''}
                              />
                            );

                          case 'phone':
                            return (
                              <TextField
                                fullWidth
                                type="tel"
                                value={field.value || ''}
                                onChange={(e) => {
                                  const updatedFields = [...editingResponse.formFields];
                                  updatedFields[index].value = e.target.value;
                                  setEditingResponse({
                                    ...editingResponse,
                                    formFields: updatedFields
                                  });
                                }}
                                variant="outlined"
                                size="small"
                                placeholder={field.placeholder || ''}
                                helperText={field.helpText || ''}
                              />
                            );

                          case 'select':
                            return (
                              <FormControl fullWidth size="small">
                                <Select
                                  value={field.value || ''}
                                  onChange={(e) => {
                                    const updatedFields = [...editingResponse.formFields];
                                    updatedFields[index].value = e.target.value;
                                    setEditingResponse({
                                      ...editingResponse,
                                      formFields: updatedFields
                                    });
                                  }}
                                  variant="outlined"
                                  displayEmpty
                                >
                                  <MenuItem value="" disabled>
                                    <em>Seçiniz</em>
                                  </MenuItem>
                                  {Array.isArray(field.options) && field.options.map((option, i) => (
                                    <MenuItem key={i} value={option.value || option}>
                                      {option.label || option}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {field.helpText && (
                                  <FormHelperText>{field.helpText}</FormHelperText>
                                )}
                              </FormControl>
                            );

                          case 'radio':
                            return (
                              <FormControl component="fieldset">
                                <RadioGroup
                                  value={field.value || ''}
                                  onChange={(e) => {
                                    const updatedFields = [...editingResponse.formFields];
                                    updatedFields[index].value = e.target.value;
                                    setEditingResponse({
                                      ...editingResponse,
                                      formFields: updatedFields
                                    });
                                  }}
                                >
                                  {Array.isArray(field.options) && field.options.map((option, i) => (
                                    <MuiFormControlLabel
                                      key={i}
                                      value={option.value || option}
                                      control={<Radio />}
                                      label={option.label || option}
                                    />
                                  ))}
                                </RadioGroup>
                                {field.helpText && (
                                  <FormHelperText>{field.helpText}</FormHelperText>
                                )}
                              </FormControl>
                            );

                          case 'checkbox':
                            return (
                              <FormGroup>
                                {Array.isArray(field.options) && field.options.map((option, i) => (
                                  <FormControlLabel
                                    key={i}
                                    control={
                                      <Checkbox
                                        checked={Array.isArray(field.value) && field.value.includes(option.value || option)}
                                        onChange={(e) => {
                                          const updatedFields = [...editingResponse.formFields];
                                          const currentValue = Array.isArray(field.value) ? field.value : [];
                                          const optionValue = option.value || option;

                                          if (e.target.checked) {
                                            updatedFields[index].value = [...currentValue, optionValue];
                                          } else {
                                            updatedFields[index].value = currentValue.filter(v => v !== optionValue);
                                          }

                                          setEditingResponse({
                                            ...editingResponse,
                                            formFields: updatedFields
                                          });
                                        }}
                                      />
                                    }
                                    label={option.label || option}
                                  />
                                ))}
                                {field.helpText && (
                                  <FormHelperText>{field.helpText}</FormHelperText>
                                )}
                              </FormGroup>
                            );

                          case 'table':
                            return (
                              <Box sx={{ mt: 2 }}>
                                <TableContainer>
                                  <Table size="small">
                                    <TableBody>
                                      {field.value?.rows?.map((row, rowIndex) => (
                                        <TableRow key={rowIndex}>
                                          {Object.entries(row).map(([key, value], cellIndex) => (
                                            <TableCell key={cellIndex}>
                                              <TextField
                                                size="small"
                                                value={value}
                                                onChange={(e) => {
                                                  const updatedFields = [...editingResponse.formFields];
                                                  const updatedRows = [...field.value.rows];
                                                  updatedRows[rowIndex] = {
                                                    ...updatedRows[rowIndex],
                                                    [key]: e.target.value
                                                  };
                                                  updatedFields[index].value = {
                                                    ...field.value,
                                                    rows: updatedRows
                                                  };
                                                  setEditingResponse({
                                                    ...editingResponse,
                                                    formFields: updatedFields
                                                  });
                                                }}
                                              />
                                            </TableCell>
                                          ))}
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                                {field.helpText && (
                                  <FormHelperText>{field.helpText}</FormHelperText>
                                )}
                              </Box>
                            );

                          default:
                            return (
                              <TextField
                                fullWidth
                                value={field.value || ''}
                                onChange={(e) => {
                                  const updatedFields = [...editingResponse.formFields];
                                  updatedFields[index].value = e.target.value;
                                  setEditingResponse({
                                    ...editingResponse,
                                    formFields: updatedFields
                                  });
                                }}
                                variant="outlined"
                                size="small"
                                placeholder={field.placeholder || ''}
                                helperText={field.helpText || ''}
                              />
                            );
                        }
                      })()
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #eee', p: 2 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            variant="outlined"
            startIcon={<CloseIcon />}
          >
            İptal
          </Button>
          <Button
            onClick={() => onSaveForm(editingResponse.formFields)}
            variant="contained"
            color="secondary"
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            {loading ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview modalı */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #eee'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PreviewIcon color="success" />
            Form Önizleme
          </Box>
          <IconButton onClick={() => setPreviewDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {previewData && (
            <Box sx={{ py: 2 }}>
              <FormResponsePreview formData={previewData} />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #eee', p: 2 }}>
          <Button
            onClick={() => setPreviewDialogOpen(false)}
            variant="outlined"
            startIcon={<CloseIcon />}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Form Dialog */}
      <DeleteFormDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={onDeleteResponse}
        response={responseToDelete}
        loading={loading}
      />
    </Box>
  );
};

export default Analytics;